import type { IssueArchivalChangeUseCase } from 'shared/domain/issue/issueArchivalChange';
import { add as addRequest } from 'serviceWorker/db/httpRequests';
import { get as getCurrentProject } from 'serviceWorker/db/selectedProject';
import { updateOne as updateIssue } from 'serviceWorker/db/issues/issues';
import { Identificable } from 'shared/types/commonView';
import { debugLog } from 'shared/logger/debugLog';
import {
  HttpRequestModelType,
  HttpRequestStatus,
  __HttpRequestModel__,
} from 'shared/domain/httpRequest/httpRequestModel';
import type { IssueRequestData } from '../httpQueue/issue/issueRequestData';

/** The local half of an archive, e.g. after the server already archived the issue. */
export function markIssueDeletedLocally(issueId: string): Promise<unknown> {
  return updateIssue(issueId, { deleted: 1 });
}

/** The local half of a restore, or the undo of an archive the server refused. */
export function markIssueRestoredLocally(issueId: string): Promise<unknown> {
  return updateIssue(issueId, { deleted: 0 });
}

/** One direction of the archival: what the server is asked and what changes on this device. */
export interface IssueArchivalDirection {
  /** Names the change in logs and errors, e.g. `archive`. */
  readonly name: string;
  readonly method: 'DELETE' | 'GET';
  toUrl(projectId: string, issueId: string): string;
  applyLocally(issueId: string): Promise<unknown>;
  undoLocally(issueId: string): Promise<unknown>;
}

export const archiveDirection: IssueArchivalDirection = {
  name: 'archive',
  method: 'DELETE',
  toUrl: (projectId, issueId) => `project/${projectId}/issue/${issueId}`,
  applyLocally: markIssueDeletedLocally,
  undoLocally: markIssueRestoredLocally,
};

const RESTORE_PATH_SUFFIX = '/restore';

export const restoreDirection: IssueArchivalDirection = {
  name: 'restore',
  method: 'GET',
  toUrl: (projectId, issueId) => `project/${projectId}/issue/${issueId}${RESTORE_PATH_SUFFIX}`,
  applyLocally: markIssueRestoredLocally,
  undoLocally: markIssueDeletedLocally,
};

/** A queued restore of an issue, not any other GET a later change may queue. */
export function isIssueRestoreRequest({ method, url }: Pick<__HttpRequestModel__, 'method' | 'url'>): boolean {
  return method === restoreDirection.method && Boolean(url?.endsWith(RESTORE_PATH_SUFFIX));
}

/**
 * Applies an archive or a restore in IndexedDB at once and queues it for the
 * server; the server's answer follows as an event with the change's `uniqueId`.
 */
export class IssueArchivalChanger implements IssueArchivalChangeUseCase {
  constructor(
    private direction: IssueArchivalDirection,
    private addRequest: (request: __HttpRequestModel__) => Promise<unknown>,
    private getCurrentProject: () => Promise<Identificable | undefined>
  ) {}

  async execute(issueId: string, uniqueId: string): Promise<void> {
    const { name, method, toUrl, applyLocally, undoLocally } = this.direction;
    debugLog(`Issue ${name}`, issueId);
    const project = await this.getCurrentProject();
    if (!project) {
      throw new Error(`Cannot ${name} the issue: no project is selected`);
    }
    const data: IssueRequestData = { uniqueId, issueId };

    await applyLocally(issueId);
    try {
      await this.addRequest({
        createdAt: Date.now(),
        method,
        url: toUrl(project._id, issueId),
        data,
        entityType: HttpRequestModelType.issue,
        status: HttpRequestStatus.NEW,
      });
    } catch (error) {
      // Not queued: the server will never apply it, so neither may this device.
      await undoLocally(issueId);
      throw error;
    }
  }
}

export const issueDeleter = new IssueArchivalChanger(archiveDirection, addRequest, getCurrentProject);
export const issueRestorer = new IssueArchivalChanger(restoreDirection, addRequest, getCurrentProject);
